import apiSvc, { AxiosEntity, UserProfilePref } from '../services/apiSvc';
import { ngDelegate } from '../services/ng.delegate';
import utilSvc from '../utilSvc';
import validSvc from '../validSvc';

const userPrefsRepo = {
    getUserPrefs,
    saveUserPrefs,
};

/**
 * Save a user preference to the database (Profile array).
 * Used for persisting table views, dashboard settings, filters, etc.
 *
 * @param prefType The type of preference (e.g., 'TABLEVIEW', 'DASHBOARD')
 * @param prefStringVal The stringified value to save (usually JSON)
 * @returns Promise<object> Resolves when the preference is saved
 */
async function saveUserPrefs(prefType: string, prefStringVal: string): Promise<void> {
    const validTypes = ['DASHBOARD', 'FILTERSWAP', 'TABLEVIEW', 'ADMIN', 'SAVEDREPORTS'];
    if (!validTypes.includes(prefType) || !validSvc.isText(prefStringVal)) {
        throw new Error('apiSvc.saveUserPref, prefType unknown: ' + prefType);
    }

    const purl = 'user/myaccount/prefsave';

    const apiRequest = {
        Entity: {
            Prefs: [{ Type: prefType, Value: prefStringVal }],
        },
    };

    try {
        const response = await apiSvc.post(purl, apiRequest);

        // bad upsert response format
        if (!validSvc.httpUpsert200Resp(response)) {
            ngDelegate.warn('Bad 200 api response format', purl + 'repo', '200', utilSvc.http.safeResponse(response));
            throw new Error('Invalid response format');
        }
    } catch (error) {
        if (error && (error as any).response) {
            ngDelegate.error(
                'Unexpected api error',
                'apiSvc/' + purl,
                (error as any).response.status,
                utilSvc.http.safeResponse((error as any).response),
            );
        }
        throw error;
    }
}

/**
 * Retrieves the user's profile preferences from the backend.
 *
 * Makes a POST request to refresh the login and extract the Profile node from the response.
 * Validates the response format and returns the array of user preferences.
 *
 * @returns Promise<UserProfilePref[]> Resolves with an array of user profile preferences, or an empty array if none found.
 * @throws Error if the API response is invalid or the request fails.
 */
async function getUserPrefs(): Promise<UserProfilePref[]> {
    // get Profile node from content returned on login
    const myPurl = 'security/login/refresh';

    try {
        const r: AxiosEntity = await apiSvc.post(myPurl, {});

        // validate login data - need to wrap response for validation function
        // The post() function returns r.data, so we need to create a full response object for validation
        const wrappedResponse = {
            status: 200, // Assume 200 since post() would have thrown on error
            data: r,
        };

        if (!validSvc.httpGetEntityResp(wrappedResponse)) {
            ngDelegate.error(
                'Bad 200 api login response format',
                'apiSvc.getUserPrefs',
                '',
                utilSvc.http.safeResponse(r),
            );
            throw utilSvc.http.ignore(undefined, 'getUserPrefs 200');
        }

        const ent: { User?: { Profile?: UserProfilePref[] } } = r.Entity;

        if (ent && ent.User && Array.isArray(ent.User.Profile)) {
            return ent.User.Profile;
        } else {
            return [];
        }
    } catch (error) {
        if (error && (error as any).response) {
            ngDelegate.error(
                'Unknown error, apiSvc.getUserPrefs',
                myPurl,
                (error as any).response.status,
                utilSvc.http.safeResponse((error as any).response),
            );
        }
        throw error;
    }
}

export default userPrefsRepo;
